/**
 * ErrorHandler - Centralized error handling and user notifications
 * Provides consistent error handling across the application
 */

export interface AppError {
  type: 'network' | 'data' | 'storage' | 'general';
  message: string;
  details?: string;
  timestamp: Date;
  userMessage: string;
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorContainer: HTMLElement | null = null;

  constructor() {
    this.createErrorContainer();
  }

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  private createErrorContainer(): void {
    // Create error notification container if it doesn't exist
    if (!document.querySelector('#error-container')) {
      const container = document.createElement('div');
      container.id = 'error-container';
      container.className = 'error-container';
      document.body.appendChild(container);
      this.errorContainer = container;
      this.addStyles();
    }
  }

  /**
   * Handle network-related errors
   */
  handleNetworkError(error: Error): AppError {
    const appError: AppError = {
      type: 'network',
      message: error.message,
      timestamp: new Date(),
      userMessage: navigator.onLine 
        ? 'Failed to connect to the server. Please try again.'
        : 'You appear to be offline. Using cached data where available.'
    };

    this.logError(appError);
    this.showErrorToUser(appError);
    return appError;
  }

  /**
   * Handle data-related errors (parsing, validation, etc.)
   */
  handleDataError(error: Error, context?: string): AppError {
    const appError: AppError = {
      type: 'data',
      message: error.message,
      details: context,
      timestamp: new Date(),
      userMessage: 'There was a problem with the data. Please refresh the page.'
    };

    this.logError(appError);
    this.showErrorToUser(appError);
    return appError;
  }

  /**
   * Handle storage-related errors
   */
  handleStorageError(error: Error): AppError {
    const appError: AppError = {
      type: 'storage',
      message: error.message,
      timestamp: new Date(),
      userMessage: 'Unable to save data locally. The app will continue to work online.'
    };

    this.logError(appError);
    this.showErrorToUser(appError, 'warning');
    return appError;
  }

  /**
   * Handle general application errors
   */
  handleGeneralError(error: Error, context?: string): AppError {
    const appError: AppError = {
      type: 'general',
      message: error.message,
      details: context,
      timestamp: new Date(),
      userMessage: 'Something went wrong. Please try refreshing the page.'
    };

    this.logError(appError);
    this.showErrorToUser(appError);
    return appError;
  }

  /**
   * Show success message to user
   */
  showSuccess(message: string): void {
    this.showNotification(message, 'success');
  }

  /**
   * Show info message to user
   */
  showInfo(message: string): void {
    this.showNotification(message, 'info');
  }

  /**
   * Show warning message to user
   */
  showWarning(message: string): void {
    this.showNotification(message, 'warning');
  }

  private logError(error: AppError): void {
    console.error(`[${error.type.toUpperCase()}] ${error.message}`, {
      details: error.details,
      timestamp: error.timestamp
    });

    // In production, this could send errors to a monitoring service
    if (this.isProduction()) {
      this.sendErrorToMonitoring(error);
    }
  }

  private showErrorToUser(error: AppError, type: 'error' | 'warning' = 'error'): void {
    this.showNotification(error.userMessage, type);
  }

  private showNotification(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    if (!this.errorContainer) return;

    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    
    const icons = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️'
    };

    notification.innerHTML = `
      <div class="notification-content">
        <span class="notification-icon">${icons[type]}</span>
        <span class="notification-message">${message}</span>
        <button class="notification-close" aria-label="Close notification">×</button>
      </div>
    `;

    // Add to container
    this.errorContainer.appendChild(notification);

    // Setup close handler
    const closeBtn = notification.querySelector('.notification-close');
    closeBtn?.addEventListener('click', () => {
      this.removeNotification(notification);
    });

    // Auto-remove after 5 seconds (except errors which need manual dismissal)
    if (type !== 'error') {
      setTimeout(() => {
        this.removeNotification(notification);
      }, 5000);
    }

    // Animate in
    setTimeout(() => {
      notification.classList.add('notification-show');
    }, 10);
  }

  private removeNotification(notification: HTMLElement): void {
    notification.classList.add('notification-hide');
    setTimeout(() => {
      notification.remove();
    }, 300);
  }

  /**
   * Clear all notifications
   */
  clearAllNotifications(): void {
    if (!this.errorContainer) return;
    this.errorContainer.innerHTML = '';
  }

  private isProduction(): boolean {
    return import.meta.env.PROD;
  }

  private sendErrorToMonitoring(error: AppError): void {
    // In a real app, this would send to a service like Sentry
    // For now, we'll just log it
    console.log('Would send error to monitoring service:', error);
  }

  /**
   * Global error handler setup
   */
  setupGlobalErrorHandling(): void {
    // Handle uncaught errors
    window.addEventListener('error', (event) => {
      this.handleGeneralError(new Error(event.message), event.filename);
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleGeneralError(new Error(event.reason), 'Unhandled Promise Rejection');
      event.preventDefault();
    });
  }

  private addStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .error-container {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 1000;
        display: flex;
        flex-direction: column;
        gap: 12px;
        max-width: 400px;
        pointer-events: none;
      }
      
      @media (max-width: 768px) {
        .error-container {
          top: 10px;
          right: 10px;
          left: 10px;
          max-width: none;
        }
      }
      
      .notification {
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        pointer-events: auto;
        transform: translateX(100%);
        transition: all 0.3s ease;
        border-left: 4px solid;
      }
      
      .notification-show {
        transform: translateX(0);
      }
      
      .notification-hide {
        transform: translateX(100%);
        opacity: 0;
      }
      
      .notification-success {
        border-left-color: #10b981;
      }
      
      .notification-error {
        border-left-color: #ef4444;
      }
      
      .notification-warning {
        border-left-color: #f59e0b;
      }
      
      .notification-info {
        border-left-color: #3b82f6;
      }
      
      .notification-content {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px;
      }
      
      .notification-icon {
        font-size: 18px;
        flex-shrink: 0;
      }
      
      .notification-message {
        flex: 1;
        font-size: 14px;
        line-height: 1.4;
        color: #374151;
      }
      
      .notification-close {
        background: none;
        border: none;
        font-size: 18px;
        cursor: pointer;
        color: #6b7280;
        padding: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: all 0.2s ease;
        flex-shrink: 0;
      }
      
      .notification-close:hover {
        background: #f3f4f6;
        color: #374151;
      }
      
      /* Accessibility improvements */
      @media (prefers-reduced-motion: reduce) {
        .notification {
          transition: none;
        }
      }
      
      /* High contrast mode */
      @media (prefers-contrast: high) {
        .notification {
          border-width: 2px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }
      }
    `;
    
    document.head.appendChild(style);
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();